import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  TestTube,
  Scroll,
  Dna,
  Package,
  ChartLine,
  Clock,
  ArrowRight,
  Lightning,
  TrendUp,
  CheckCircle,
  CircleDashed,
  XCircle,
  Warning,
  CalendarBlank
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#6366F1', '#22D3EE', '#FBBF24', '#A78BFA', '#EC4899', '#EF4444'];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentExperiments, setRecentExperiments] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getDashboardStats().catch(() => null),
      api.getExperiments({ limit: 5 }).catch(() => []),
      api.getInventory().catch(() => [])
    ]).then(([s, exps, inv]) => {
      setStats(s);
      setRecentExperiments(Array.isArray(exps) ? exps.slice(0, 5) : []);
      setLowStock(Array.isArray(inv) ? inv.filter(i => i.quantity <= (i.minimum_quantity || 5)).slice(0, 5) : []);
      setLoading(false);
    });
  }, []);

  const statusIcon = (status) => {
    if (status === 'completed') return <CheckCircle size={14} weight="fill" className="text-emerald-400" />;
    if (status === 'in_progress') return <CircleDashed size={14} className="text-indigo-400" />;
    if (status === 'failed') return <XCircle size={14} className="text-red-400" />;
    return <Clock size={14} style={{ color: '#94A3B8' }} />;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const statCards = [
    { label: 'Experiments', value: stats?.experiments?.total ?? stats?.experiments ?? 0, icon: TestTube, color: '#6366F1', path: '/experiments' },
    { label: 'Protocols', value: stats?.protocols || 0, icon: Scroll, color: '#22D3EE', path: '/protocols' },
    { label: 'Plasmids', value: stats?.plasmids || 0, icon: Dna, color: '#A78BFA', path: '/plasmids' },
    { label: 'Inventory Items', value: stats?.inventory_items || 0, icon: Package, color: '#FBBF24', path: '/inventory' },
  ];

  const expObj = typeof stats?.experiments === 'object' ? stats.experiments : {};
  const expStatusData = [
    { name: 'Active', value: expObj.active || 0 },
    { name: 'Completed', value: expObj.completed || 0 },
    { name: 'Failed', value: expObj.failed || 0 },
  ].filter(d => d.value > 0);

  return (
    <div data-testid="dashboard-page">
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="heading-display">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p style={{ color: '#94A3B8' }} className="mt-1">Here's what's happening in the lab today</p>
      </div>

      {/* Stat Cards */}
      <div className="dashboard-grid mb-6">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="stat-card cursor-pointer group"
            onClick={() => navigate(card.path)}
            data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${card.color}18` }}>
                <card.icon size={20} style={{ color: card.color }} weight="duotone" />
              </div>
              <ArrowRight size={14} style={{ color: '#64748B' }} className="group-hover:text-indigo-400 transition-colors" />
            </div>
            <p className="text-2xl font-bold" style={{ color: '#F8FAFC' }}>{card.value}</p>
            <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{card.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent Experiments */}
          <div className="card-flat">
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
              <h2 className="heading-card">Recent Experiments</h2>
              <button onClick={() => navigate('/experiments')} className="text-xs font-medium flex items-center gap-1 hover:underline" style={{ color: '#818CF8' }}>
                View all <ArrowRight size={12} />
              </button>
            </div>
            {recentExperiments.length > 0 ? (
              <div>
                {recentExperiments.map(exp => (
                  <div key={exp.id} className="flex items-center gap-3 px-5 py-3 transition-colors cursor-pointer" style={{ borderBottom: '1px solid rgba(148,163,184,0.06)' }} onClick={() => navigate(`/experiments/${exp.id}`)}>
                    {statusIcon(exp.status)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#F1F5F9' }}>{exp.title}</p>
                      <p className="text-[11px]" style={{ color: '#94A3B8' }}>{exp.experiment_type} &middot; {new Date(exp.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`status-${exp.status?.replace('_', '-') || 'planned'} text-[10px]`}>{exp.status?.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10" style={{ color: '#94A3B8' }}>
                <TestTube size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No experiments yet</p>
              </div>
            )}
          </div>

          {/* Experiment Status Chart */}
          {expStatusData.length > 0 && (
            <div className="card-flat p-5">
              <h2 className="heading-card mb-4">Experiment Overview</h2>
              <div className="grid grid-cols-2 gap-6">
                <div style={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={expStatusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                        {expStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: '#1E293B', border: '1px solid rgba(148,163,184,0.15)', borderRadius: '8px', color: '#E2E8F0' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-col justify-center space-y-2">
                  {expStatusData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2 text-sm">
                      <div className="w-3 h-3 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="flex-1" style={{ color: '#CBD5E1' }}>{d.name}</span>
                      <span className="font-semibold" style={{ color: '#F8FAFC' }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Low Stock Alerts */}
          <div className="card-flat p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="heading-card flex items-center gap-2"><Warning size={16} className="text-amber-400" /> Low Stock Alerts</h2>
              <button onClick={() => navigate('/inventory')} className="text-xs font-medium hover:underline" style={{ color: '#818CF8' }}>View inventory</button>
            </div>
            {lowStock.length > 0 ? (
              <div className="space-y-2">
                {lowStock.map(item => (
                  <div key={item.id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ border: '1px solid rgba(251,191,36,0.2)', background: 'rgba(251,191,36,0.06)' }}>
                    <Package size={14} className="text-amber-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: '#F1F5F9' }}>{item.name}</p>
                      <p className="text-[11px]" style={{ color: '#FBBF24' }}>{item.quantity} {item.unit} remaining</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs py-4 text-center" style={{ color: '#94A3B8' }}>All items in stock</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card-flat p-5">
            <h2 className="heading-card mb-3">Quick Actions</h2>
            <div className="space-y-1.5">
              {[
                { label: 'New Experiment', path: '/experiments', icon: TestTube },
                { label: 'New Protocol', path: '/protocols', icon: Scroll },
                { label: 'Figure Maker', path: '/figures', icon: ChartLine },
                { label: 'View Calendar', path: '/calendar', icon: CalendarBlank },
              ].map(a => (
                <button key={a.label} onClick={() => navigate(a.path)} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg transition-colors text-left" style={{ color: '#CBD5E1' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.color = '#A5B4FC'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#CBD5E1'; }}
                >
                  <a.icon size={16} /> {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
